import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeftRight,
  Clock,
  User,
  Calendar,
  CheckCircle,
  XCircle,
  Loader2,
  Plus,
  RefreshCw,
  Bell,
  BellOff,
} from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { useNotification } from '../hooks/useNotification';
import ShiftSwitchRequestForm from '../components/ShiftSwitchRequestForm';
import Ably from 'ably';

interface ShiftSwitchRequest {
  id: string;
  requesterId: string;
  requesterName: string;
  targetStaffId: string;
  targetStaffName: string;
  originalShiftId: string;
  originalShiftTitle: string;
  originalShiftDate: string;
  originalShiftTime: string;
  requestedShiftId: string;
  requestedShiftTitle: string;
  requestedShiftDate: string;
  requestedShiftTime: string;
  status: 'pending' | 'accepted' | 'rejected' | 'cancelled';
  reason?: string;
  createdAt: string;
}

interface Shift {
  id: string;
  title: string;
  date: string;
  startTime: string;
  endTime: string;
  staffId: string;
  staffName: string;
  status: string;
}

interface StaffMember {
  id: string;
  name: string;
  email: string;
  position: string;
}

const ModernShiftSwitching: React.FC = () => {
  const { translate } = useTranslation();
  const { notify } = useNotification();
  const [activeTab, setActiveTab] = useState<
    'my-requests' | 'received-requests' | 'available-shifts'
  >('my-requests');

  const [myRequests, setMyRequests] = useState<ShiftSwitchRequest[]>([]);
  const [receivedRequests, setReceivedRequests] = useState<
    ShiftSwitchRequest[]
  >([]);
  const [myShifts, setMyShifts] = useState<Shift[]>([]);
  const [availableShifts, setAvailableShifts] = useState<Shift[]>([]);
  const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [realtimeEnabled, setRealtimeEnabled] = useState(false);
  const [ablyClient, setAblyClient] = useState<Ably.Realtime | null>(null);

  // Cache tracking to prevent unnecessary API calls
  const [dataCache, setDataCache] = useState({
    myRequests: false,
    receivedRequests: false,
    myShifts: false,
    availableShifts: false,
    staffMembers: false,
  });

  // Fetch my requests
  const fetchMyRequests = async (forceRefresh = false) => {
    if (dataCache.myRequests && !forceRefresh) return;

    try {
      setLoading(true);
      const response = await fetch('/shift_switch_requests/my-requests', {
        headers: {
          Authorization: `Bearer ${
            JSON.parse(localStorage.getItem('auth.accessToken') || '{}')
              .value || localStorage.getItem('token')
          }`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setMyRequests(data);
        setDataCache((prev) => ({ ...prev, myRequests: true }));
      } else {
        console.error('Failed to fetch my requests');
        notify(
          translate(
            'pos.staff.shift_switching.failed_load_requests',
            'Failed to load your shift switch requests'
          ),
          { type: 'error' }
        );
      }
    } catch (error) {
      console.error('Error fetching my requests:', error);
      notify(
        translate(
          'pos.staff.shift_switching.error_load_requests',
          'Error loading your shift switch requests'
        ),
        { type: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch received requests
  const fetchReceivedRequests = async (forceRefresh = false) => {
    if (dataCache.receivedRequests && !forceRefresh) return;

    try {
      setLoading(true);
      const response = await fetch('/shift_switch_requests/received', {
        headers: {
          Authorization: `Bearer ${
            JSON.parse(localStorage.getItem('auth.accessToken') || '{}')
              .value || localStorage.getItem('token')
          }`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setReceivedRequests(data);
        setDataCache((prev) => ({ ...prev, receivedRequests: true }));
      } else {
        console.error('Failed to fetch received requests');
        notify(
          translate(
            'pos.staff.shift_switching.failed_load_received',
            'Failed to load received shift switch requests'
          ),
          { type: 'error' }
        );
      }
    } catch (error) {
      console.error('Error fetching received requests:', error);
      notify(
        translate(
          'pos.staff.shift_switching.error_load_received',
          'Error loading received shift switch requests'
        ),
        { type: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch my shifts
  const fetchMyShifts = async (forceRefresh = false) => {
    if (dataCache.myShifts && !forceRefresh) return;

    try {
      setLoading(true);
      const token =
        JSON.parse(localStorage.getItem('auth.accessToken') || '{}').value ||
        localStorage.getItem('token');
      console.log(
        '🔑 Fetching my shifts with token:',
        token ? 'Token present' : 'No token'
      );

      const response = await fetch('/api/shifts', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log(
        '📡 My shifts API response:',
        response.status,
        response.statusText
      );

      if (response.ok) {
        const data = await response.json();
        console.log('📊 My shifts data received:', data.length, 'shifts');
        setMyShifts(data);
        setDataCache((prev) => ({ ...prev, myShifts: true }));
      } else {
        console.error(
          'Failed to fetch my shifts:',
          response.status,
          response.statusText
        );
        notify(
          translate(
            'pos.staff.shift_switching.failed_load_shifts',
            'Failed to load your shifts'
          ),
          { type: 'error' }
        );
      }
    } catch (error) {
      console.error('Error fetching my shifts:', error);
      notify(
        translate(
          'pos.staff.shift_switching.error_load_shifts',
          'Error loading your shifts'
        ),
        { type: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch available shifts for switching
  const fetchAvailableShifts = async (forceRefresh = false) => {
    if (dataCache.availableShifts && !forceRefresh) return;

    try {
      setLoading(true);
      const token =
        JSON.parse(localStorage.getItem('auth.accessToken') || '{}').value ||
        localStorage.getItem('token');
      console.log(
        '🔑 Fetching available shifts with token:',
        token ? 'Token present' : 'No token'
      );

      const response = await fetch('/api/available-shifts', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log(
        '📡 Available shifts API response:',
        response.status,
        response.statusText
      );

      if (response.ok) {
        const data = await response.json();
        console.log(
          '📊 Available shifts data received:',
          data.length,
          'shifts'
        );
        setAvailableShifts(data);
        setDataCache((prev) => ({ ...prev, availableShifts: true }));
      } else {
        console.error(
          'Failed to fetch available shifts:',
          response.status,
          response.statusText
        );
        notify(
          translate(
            'pos.staff.shift_switching.failed_load_available',
            'Failed to load available shifts'
          ),
          { type: 'error' }
        );
      }
    } catch (error) {
      console.error('Error fetching available shifts:', error);
      notify(
        translate(
          'pos.staff.shift_switching.error_load_available',
          'Error loading available shifts'
        ),
        { type: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch staff members
  const fetchStaffMembers = async (forceRefresh = false) => {
    if (dataCache.staffMembers && !forceRefresh) return;

    try {
      setLoading(true);
      const response = await fetch('/api/staff', {
        headers: {
          Authorization: `Bearer ${
            JSON.parse(localStorage.getItem('auth.accessToken') || '{}')
              .value || localStorage.getItem('token')
          }`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStaffMembers(data);
        setDataCache((prev) => ({ ...prev, staffMembers: true }));
      } else {
        console.error('Failed to fetch staff members');
        notify(
          translate(
            'pos.staff.shift_switching.failed_load_staff',
            'Failed to load staff members'
          ),
          { type: 'error' }
        );
      }
    } catch (error) {
      console.error('Error fetching staff members:', error);
      notify(
        translate(
          'pos.staff.shift_switching.error_load_staff',
          'Error loading staff members'
        ),
        { type: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  // Initialize Ably connection for real-time notifications
  const initializeAbly = useCallback(async () => {
    try {
      const ablyApiKey = import.meta.env.VITE_ABLY_API_KEY;
      if (!ablyApiKey) {
        console.log(
          '⚠️ Ably API key not found, real-time notifications disabled'
        );
        return;
      }

      const client = new Ably.Realtime({
        key: ablyApiKey,
        clientId: `shift-switching-${Date.now()}`, // Unique client ID
      });

      client.connection.on('connected', () => {
        console.log('✅ Ably connected for shift switching');
        setRealtimeEnabled(true);
      });

      client.connection.on('disconnected', () => {
        console.log('❌ Ably disconnected for shift switching');
        setRealtimeEnabled(false);
      });

      setAblyClient(client);
    } catch (error) {
      console.error('❌ Failed to initialize Ably:', error);
    }
  }, []);

  // Set up real-time subscriptions
  const setupRealtimeSubscriptions = useCallback(() => {
    if (!ablyClient) return;

    try {
      // Subscribe to user-specific notifications
      const userChannel = ablyClient.channels.get(
        `shift-switching-${localStorage.getItem('userId') || 'anonymous'}`
      );

      userChannel.subscribe('request-accepted', (message) => {
        console.log('📨 Received request accepted notification:', message.data);
        notify(message.data.message, { type: 'success' });
        // Refresh data to show updated status
        fetchMyRequests(true);
      });

      userChannel.subscribe('request-rejected', (message) => {
        console.log('📨 Received request rejected notification:', message.data);
        notify(message.data.message, { type: 'warning' });
        // Refresh data to show updated status
        fetchMyRequests(true);
      });

      userChannel.subscribe('request-cancelled', (message) => {
        console.log(
          '📨 Received request cancelled notification:',
          message.data
        );
        notify(message.data.message, { type: 'info' });
        // Refresh received requests
        fetchReceivedRequests(true);
      });

      userChannel.subscribe('new-request', (message) => {
        console.log('📨 Received new request notification:', message.data);
        notify(message.data.message, { type: 'info' });
        // Refresh received requests
        fetchReceivedRequests(true);
      });

      // Subscribe to broadcast notifications
      const broadcastChannel = ablyClient.channels.get(
        'shift-switching-broadcast'
      );

      broadcastChannel.subscribe('shift-switched', (message) => {
        console.log('📨 Received shift switched broadcast:', message.data);
        notify(message.data.message, { type: 'success' });
        // Refresh all data
        fetchMyRequests(true);
        fetchReceivedRequests(true);
        fetchAvailableShifts(true);
      });

      broadcastChannel.subscribe('shift-available', (message) => {
        console.log('📨 Received shift available broadcast:', message.data);
        notify(message.data.message, { type: 'info' });
        // Refresh available shifts
        fetchAvailableShifts(true);
      });

      broadcastChannel.subscribe('open-request', (message) => {
        console.log('📨 Received open request broadcast:', message.data);
        notify(message.data.message, { type: 'info' });
        // Refresh available shifts
        fetchAvailableShifts(true);
      });

      console.log('✅ Real-time subscriptions set up for shift switching');

      // Return cleanup function
      return () => {
        try {
          userChannel.unsubscribe();
          userChannel.detach();
          broadcastChannel.unsubscribe();
          broadcastChannel.detach();
        } catch (error) {
          console.error('Error cleaning up Ably channels:', error);
        }
      };
    } catch (error) {
      console.error('❌ Failed to set up real-time subscriptions:', error);
    }
  }, [
    ablyClient,
    notify,
    fetchMyRequests,
    fetchReceivedRequests,
    fetchAvailableShifts,
  ]);

  // Load data on component mount - only load essential data
  useEffect(() => {
    fetchMyRequests();
    fetchStaffMembers(); // Always load staff members for form
    initializeAbly();
  }, [initializeAbly]);

  // Set up real-time subscriptions when Ably client is ready
  useEffect(() => {
    if (ablyClient) {
      const cleanup = setupRealtimeSubscriptions();

      // Return cleanup function to unsubscribe from channels
      return () => {
        if (cleanup) {
          cleanup();
        }
      };
    }
  }, [ablyClient, setupRealtimeSubscriptions]);

  // Cleanup Ably connection on unmount
  useEffect(() => {
    return () => {
      if (ablyClient) {
        try {
          // Unsubscribe from specific channels we know about
          const userId = localStorage.getItem('userId') || 'anonymous';
          const userChannelName = `shift-switching-${userId}`;
          const broadcastChannelName = 'shift-switching-broadcast';

          try {
            const userChannel = ablyClient.channels.get(userChannelName);
            userChannel.unsubscribe();
            userChannel.detach();
          } catch (error) {
            console.error('Error cleaning up user channel:', error);
          }

          try {
            const broadcastChannel =
              ablyClient.channels.get(broadcastChannelName);
            broadcastChannel.unsubscribe();
            broadcastChannel.detach();
          } catch (error) {
            console.error('Error cleaning up broadcast channel:', error);
          }
          // Close the connection
          ablyClient.close();
        } catch (error) {
          console.error('Error closing Ably connection:', error);
        }
      }
    };
  }, [ablyClient]);

  // Load tab-specific data only when explicitly switching to a tab
  const handleTabChange = (
    tab: 'my-requests' | 'received-requests' | 'available-shifts'
  ) => {
    setActiveTab(tab);

    // Only fetch data if not already cached
    if (tab === 'my-requests') {
      fetchMyRequests();
    } else if (tab === 'received-requests') {
      fetchReceivedRequests();
    } else if (tab === 'available-shifts') {
      fetchAvailableShifts();
      fetchMyShifts(); // Load my shifts for the form
    }
  };

  // Manual refresh functions for each tab
  const refreshCurrentTab = () => {
    if (activeTab === 'my-requests') {
      fetchMyRequests(true);
    } else if (activeTab === 'received-requests') {
      fetchReceivedRequests(true);
    } else if (activeTab === 'available-shifts') {
      fetchAvailableShifts(true);
      fetchMyShifts(true);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return translate('pos.staff.shift_switching.status_pending', 'Pending');
      case 'accepted':
        return translate(
          'pos.staff.shift_switching.status_accepted',
          'Accepted'
        );
      case 'rejected':
        return translate(
          'pos.staff.shift_switching.status_rejected',
          'Rejected'
        );
      case 'cancelled':
        return translate(
          'pos.staff.shift_switching.status_cancelled',
          'Cancelled'
        );
      default:
        return status;
    }
  };

  const handleAcceptRequest = async (requestId: string) => {
    try {
      setLoading(true);
      const response = await fetch(
        `/shift_switch_requests/${requestId}/accept`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        notify(
          translate(
            'pos.staff.shift_switching.accept_success',
            'Shift switch request accepted successfully!'
          ),
          { type: 'success' }
        );
        // Refresh the data
        await fetchReceivedRequests(true);
      } else {
        const error = await response.json();
        notify(
          error.message ||
            translate(
              'pos.staff.shift_switching.accept_failed',
              'Failed to accept request'
            ),
          { type: 'error' }
        );
      }
    } catch (error) {
      console.error('Error accepting request:', error);
      notify(
        translate(
          'pos.staff.shift_switching.accept_error',
          'Error accepting shift switch request'
        ),
        { type: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRejectRequest = async (requestId: string) => {
    try {
      setLoading(true);
      const response = await fetch(
        `/shift_switch_requests/${requestId}/reject`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        notify(
          translate(
            'pos.staff.shift_switching.reject_success',
            'Shift switch request rejected'
          ),
          { type: 'info' }
        );
        // Refresh the data
        await fetchReceivedRequests(true);
      } else {
        const error = await response.json();
        notify(
          error.message ||
            translate(
              'pos.staff.shift_switching.reject_failed',
              'Failed to reject request'
            ),
          { type: 'error' }
        );
      }
    } catch (error) {
      console.error('Error rejecting request:', error);
      notify(
        translate(
          'pos.staff.shift_switching.reject_error',
          'Error rejecting shift switch request'
        ),
        { type: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRequest = async (requestId: string) => {
    try {
      setLoading(true);
      const response = await fetch(
        `/shift_switch_requests/${requestId}/cancel`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        notify(
          translate(
            'pos.staff.shift_switching.cancel_success',
            'Shift switch request cancelled'
          ),
          { type: 'info' }
        );
        // Refresh the data
        await fetchMyRequests(true);
      } else {
        const error = await response.json();
        notify(
          error.message ||
            translate(
              'pos.staff.shift_switching.cancel_failed',
              'Failed to cancel request'
            ),
          { type: 'error' }
        );
      }
    } catch (error) {
      console.error('Error cancelling request:', error);
      notify(
        translate(
          'pos.staff.shift_switching.cancel_error',
          'Error cancelling shift switch request'
        ),
        { type: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle creating new shift switch request
  const handleCreateRequest = async (requestData: any) => {
    try {
      setLoading(true);
      const response = await fetch('/shift_switch_requests/create', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        const createdRequest = await response.json();
        notify(
          translate(
            'pos.staff.shift_switching.create_success',
            'Shift switch request created successfully!'
          ),
          { type: 'success' }
        );

        // Refresh the data (real-time updates will also trigger via Ably)
        await fetchMyRequests(true);
        await fetchAvailableShifts(true);

        // If it's a targeted request, also refresh received requests
        if (requestData.targetStaffId) {
          await fetchReceivedRequests(true);
        }
      } else {
        const error = await response.json();
        notify(
          error.message ||
            translate(
              'pos.staff.shift_switching.create_failed',
              'Failed to create request'
            ),
          { type: 'error' }
        );
      }
    } catch (error) {
      console.error('Error creating request:', error);
      notify(
        translate(
          'pos.staff.shift_switching.create_error',
          'Error creating shift switch request'
        ),
        { type: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const renderRequestCard = (
    request: ShiftSwitchRequest,
    isReceived: boolean = false
  ) => (
    <Card key={request.id} className="mb-4">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <ArrowLeftRight className="h-5 w-5" />
            <span>
              {translate(
                'pos.staff.shift_switching.request_card_title',
                'Shift Switch Request'
              )}
            </span>
          </CardTitle>
          <Badge className={getStatusColor(request.status)}>
            {getStatusLabel(request.status)}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Request Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h4 className="font-medium text-sm text-gray-600">
                {translate(
                  'pos.staff.shift_switching.your_shift',
                  'Your Shift'
                )}
              </h4>
              <div className="p-3 bg-blue-50 rounded-lg">
                <div className="font-medium">{request.originalShiftTitle}</div>
                <div className="text-sm text-gray-600 flex items-center space-x-1">
                  <Calendar className="h-4 w-4" />
                  <span>{request.originalShiftDate}</span>
                </div>
                <div className="text-sm text-gray-600 flex items-center space-x-1">
                  <Clock className="h-4 w-4" />
                  <span>{request.originalShiftTime}</span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium text-sm text-gray-600">
                {isReceived
                  ? translate(
                      'pos.staff.shift_switching.requested_shift',
                      'Requested Shift'
                    )
                  : translate(
                      'pos.staff.shift_switching.target_shift',
                      'Target Shift'
                    )}
              </h4>
              <div className="p-3 bg-green-50 rounded-lg">
                <div className="font-medium">{request.requestedShiftTitle}</div>
                <div className="text-sm text-gray-600 flex items-center space-x-1">
                  <Calendar className="h-4 w-4" />
                  <span>{request.requestedShiftDate}</span>
                </div>
                <div className="text-sm text-gray-600 flex items-center space-x-1">
                  <Clock className="h-4 w-4" />
                  <span>{request.requestedShiftTime}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Staff Information */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <User className="h-4 w-4 text-gray-500" />
              <span className="text-sm">
                {isReceived
                  ? translate('pos.staff.shift_switching.from', 'From:')
                  : translate('pos.staff.shift_switching.to', 'To:')}{' '}
                {isReceived ? request.requesterName : request.targetStaffName}
              </span>
            </div>
          </div>

          {/* Reason */}
          {request.reason && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="text-sm font-medium text-gray-600 mb-1">
                {translate('pos.staff.shift_switching.reason', 'Reason:')}
              </div>
              <div className="text-sm text-gray-700">{request.reason}</div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center space-x-2">
            {isReceived && request.status === 'pending' && (
              <>
                <Button
                  size="sm"
                  onClick={() => handleAcceptRequest(request.id)}
                  className="bg-green-600 hover:bg-green-700"
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-1" />
                  )}
                  {translate('pos.staff.shift_switching.accept', 'Accept')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleRejectRequest(request.id)}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <XCircle className="h-4 w-4 mr-1" />
                  )}
                  {translate('pos.staff.shift_switching.reject', 'Reject')}
                </Button>
              </>
            )}

            {!isReceived && request.status === 'pending' && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleCancelRequest(request.id)}
                className="text-gray-600"
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <XCircle className="h-4 w-4 mr-1" />
                )}
                {translate(
                  'pos.staff.shift_switching.cancel_request',
                  'Cancel Request'
                )}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6 py-6">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              <ArrowLeftRight className="h-8 w-8" />
              {translate('pos.staff.shift_switching.title', 'Shift Switching')}
            </h1>
            <p className="text-muted-foreground">
              {translate(
                'pos.staff.shift_switching.subtitle',
                'Request and manage your shift exchanges with other staff members'
              )}
            </p>
          </div>

          {/* Real-time status indicator */}
          <div className="flex items-center gap-2">
            {realtimeEnabled ? (
              <div className="flex items-center gap-1 text-green-600">
                <Bell className="h-4 w-4" />
                <span className="text-sm font-medium">
                  {translate(
                    'pos.staff.shift_switching.live_updates',
                    'Live Updates'
                  )}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-1 text-gray-500">
                <BellOff className="h-4 w-4" />
                <span className="text-sm font-medium">
                  {translate('pos.staff.shift_switching.offline', 'Offline')}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {/* Tab Navigation */}
        <div className="flex justify-between items-center">
          <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
            <button
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'my-requests'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              onClick={() => handleTabChange('my-requests')}
            >
              {translate(
                'pos.staff.shift_switching.tab_my_requests',
                'My Requests'
              )}
            </button>
            <button
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'received-requests'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              onClick={() => handleTabChange('received-requests')}
            >
              {translate(
                'pos.staff.shift_switching.tab_received_requests',
                'Received Requests'
              )}
            </button>
            <button
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'available-shifts'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              onClick={() => handleTabChange('available-shifts')}
            >
              {translate(
                'pos.staff.shift_switching.tab_available_shifts',
                'Available Shifts'
              )}
            </button>
          </div>

          <Button
            onClick={() => {
              setShowCreateForm(true);
              // Load data for the form
              fetchMyShifts();
              fetchAvailableShifts();
              fetchStaffMembers();
            }}
            className="flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>
              {translate(
                'pos.staff.shift_switching.create_request',
                'Create Request'
              )}
            </span>
          </Button>
        </div>

        {/* Content */}
        {activeTab === 'my-requests' && (
          <div>
            <div className="mb-4 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold">
                  {translate(
                    'pos.staff.shift_switching.my_requests_title',
                    'My Shift Switch Requests'
                  )}
                </h3>
                <p className="text-sm text-gray-600">
                  {translate(
                    'pos.staff.shift_switching.my_requests_description',
                    "Requests you've made to switch shifts with other staff members"
                  )}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={refreshCurrentTab}
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                {translate('pos.staff.common.refresh', 'Refresh')}
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-8 text-gray-500 flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                {translate(
                  'pos.staff.shift_switching.loading_requests',
                  'Loading your requests...'
                )}
              </div>
            ) : myRequests.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {translate(
                  'pos.staff.shift_switching.no_requests',
                  'No shift switch requests found'
                )}
              </div>
            ) : (
              myRequests.map((request) => renderRequestCard(request, false))
            )}
          </div>
        )}

        {activeTab === 'received-requests' && (
          <div>
            <div className="mb-4 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold">
                  {translate(
                    'pos.staff.shift_switching.received_requests_title',
                    'Received Shift Switch Requests'
                  )}
                </h3>
                <p className="text-sm text-gray-600">
                  {translate(
                    'pos.staff.shift_switching.received_requests_description',
                    'Requests from other staff members to switch shifts with you'
                  )}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={refreshCurrentTab}
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                {translate('pos.staff.common.refresh', 'Refresh')}
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-8 text-gray-500 flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                {translate(
                  'pos.staff.shift_switching.loading_received',
                  'Loading received requests...'
                )}
              </div>
            ) : receivedRequests.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {translate(
                  'pos.staff.shift_switching.no_received',
                  'No received requests found'
                )}
              </div>
            ) : (
              receivedRequests.map((request) =>
                renderRequestCard(request, true)
              )
            )}
          </div>
        )}

        {activeTab === 'available-shifts' && (
          <div>
            <div className="mb-4 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold">
                  {translate(
                    'pos.staff.shift_switching.available_shifts_title',
                    'Available Shifts for Switching'
                  )}
                </h3>
                <p className="text-sm text-gray-600">
                  {translate(
                    'pos.staff.shift_switching.available_shifts_description',
                    'Shifts that are available for switching with other staff members'
                  )}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchAvailableShifts(true)}
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                {translate('pos.staff.common.refresh', 'Refresh')}
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-8 text-gray-500 flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                {translate(
                  'pos.staff.shift_switching.loading_shifts',
                  'Loading available shifts...'
                )}
              </div>
            ) : availableShifts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {translate(
                  'pos.staff.shift_switching.no_available_shifts',
                  'No available shifts for switching'
                )}
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {availableShifts.map((shift) => (
                  <Card
                    key={shift.id}
                    className="hover:shadow-md transition-shadow"
                  >
                    <CardHeader>
                      <CardTitle className="flex items-center space-x-2">
                        <Clock className="h-5 w-5" />
                        <span>{shift.title}</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <Calendar className="h-4 w-4" />
                          <span>{shift.date}</span>
                        </div>
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <Clock className="h-4 w-4" />
                          <span>
                            {shift.startTime} - {shift.endTime}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <User className="h-4 w-4" />
                          <span>
                            {shift.staffName ||
                              translate(
                                'pos.staff.shift_switching.unassigned',
                                'Unassigned'
                              )}
                          </span>
                        </div>
                        <div className="mt-3">
                          <Badge className="bg-blue-100 text-blue-800">
                            {shift.status}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Request Form Dialog */}
      <ShiftSwitchRequestForm
        isOpen={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        onSubmit={handleCreateRequest}
        myShifts={myShifts}
        availableShifts={availableShifts}
        staffMembers={staffMembers}
      />
    </div>
  );
};

export default ModernShiftSwitching;
